import { useMutation, useQueryClient } from '@tanstack/react-query';
import { knowledgeService } from '../../../services/KnowledgeService';
import type {
  KnowledgeBaseMembership,
  KnowledgeMemberRole,
} from '../../../types/knowledge';
import { knowledgeQueryKeys } from '../knowledgeQueryKeys';
import { invalidateKnowledgeDatabaseSummaries } from '../knowledgeQueryInvalidation';

export function useInviteKnowledgeMemberMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      databaseId,
      userId,
      role,
    }: {
      databaseId: string;
      userId: string;
      role: KnowledgeMemberRole;
    }) => knowledgeService.inviteMember(databaseId, userId, role),
    onSuccess: (_result, variables) => {
      void queryClient.invalidateQueries({
        queryKey: knowledgeQueryKeys.members(variables.databaseId),
      });
      invalidateKnowledgeDatabaseSummaries(queryClient, variables.databaseId);
    },
  });
}

export function useUpdateKnowledgeMemberRoleMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      databaseId,
      member,
      role,
    }: {
      databaseId: string;
      member: KnowledgeBaseMembership;
      role: KnowledgeMemberRole;
    }) => knowledgeService.updateMemberRole(databaseId, member, role),
    onSuccess: (_result, variables) => {
      void queryClient.invalidateQueries({
        queryKey: knowledgeQueryKeys.members(variables.databaseId),
      });
      invalidateKnowledgeDatabaseSummaries(queryClient, variables.databaseId);
    },
  });
}

export function useRemoveKnowledgeMemberMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      databaseId,
      memberId,
    }: {
      databaseId: string;
      memberId: string;
    }) => knowledgeService.removeMember(databaseId, memberId),
    onSuccess: (_result, variables) => {
      void queryClient.invalidateQueries({
        queryKey: knowledgeQueryKeys.members(variables.databaseId),
      });
      invalidateKnowledgeDatabaseSummaries(queryClient, variables.databaseId);
    },
  });
}
